import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { LikutTaskScanHistoryComponent } from '@components/likut-task/likut-task-scan-history/likut-task-scan-history.component';
import { LikutProductDisplayNewRoute } from '@components/likut-task/likut-task.component';
import {
	LikutBarcodeDefinition,
	LikutBarcodeDefinitionPayload,
	LikutBarcodeScanItem,
	LikutFinishBodyNewRoutes,
	LikutFinishBodyNewRoutesScansTable,
	//likut-scan
	LikutFinishBodyNewRoutesSubTable,
	LikutKabalaSubSQL,
	LikutMekabelFinishBody,
	LikutMekabelFinishBodyForUnexistingTask,
	LikutMekabelFinishBodySubTableForNewTask,
	LikutProductBarcode,
	LikutProductBarcodePayload,
	LikutProductBarcodeWithDefinition,
	LikutScanParameterKeys,
	LikutSubSQL,
	scanHistoryOriginSource,
	ScanHistoryOriginSource,
} from '@interfaces/likut';
import { compress, decompress } from 'lz-string';
import { BehaviorSubject, catchError, combineLatest, map, Observable, take, takeUntil, throwError } from 'rxjs';
import { LikutService } from './likut.service';
import { v4 } from 'uuid';
import { translateDateForSQLServer } from '@utils/date_functions/SQL_ISO_adapter';
import { DuplicationError, IllegalActionError, IllegalStateError, ValidationError } from '@utils/Errors';
import { StoreService } from './store.service';
import { LikutTaskManuallyAddScanComponent } from '@components/likut-task/likut-task-manually-add-scan/likut-task-manually-add-scan.component';
import { SQLError, SQLResponse } from '@interfaces/SQL-interfaces';
import { getLineIdentifier } from '@components/likut-task/methods';
import { LikutSfiraPayload, LikutSfiraScansPayload, LikutSfiraSubPayload } from '@interfaces/likut-sfira';

@Injectable({
	providedIn: 'root',
})
export class LikutScanService {
	private _likut = inject(LikutService);
	private _ss = inject(StoreService);
	private dialog = inject(MatDialog);
	constructor() {}

	private readonly pathEntry = 'mobil/likut';

	barcodeDefinitions$ = new BehaviorSubject<LikutBarcodeDefinition[]>([]);
	barcodeToProducts$ = new BehaviorSubject<LikutProductBarcode[]>([]);

	parameters: Record<LikutScanParameterKeys, boolean> = {
		shouldIgnoreScanListeningWhilePopupOpen: false,
	};

	barcodeToProductsState$: Observable<LikutProductBarcodeWithDefinition[]> = combineLatest([this.barcodeToProducts$, this.barcodeDefinitions$]).pipe(
		map(([barcodeToProducts, barcodeDefinitions]) => {
			return barcodeToProducts.map(productWithBarcode => {
				const definition = barcodeDefinitions.find(def => def.Id === productWithBarcode.BarcodeDefId);
				return { definition, productWithBarcode };
			});
		}),
	);

	likutBarcodeScans$ = new BehaviorSubject<LikutBarcodeScanItem[]>([]);
	likutBarcodeScansDictionary$ = this.likutBarcodeScans$.pipe(
		map(scans =>
			scans.reduce((acc, scan) => {
				const key = this.getScanIdentifierForTask(scan);
				acc[key] ||= [];
				acc[key].push(scan);
				return acc;
			}, {} as Record<string, LikutBarcodeScanItem[]>),
		),
	);

	filterScansBy$ = new BehaviorSubject<string | null>(null);
	likutBarcodeScansDisplay$ = combineLatest([this.likutBarcodeScans$, this.filterScansBy$]).pipe(
		map(([scans, filter]) => {
			return scans.filter(scan => (filter ? scan.teudanum + scan.rowId === filter : true));
		}),
	);

	// storage and fetch
	updateStorage(options: { barcodeDefinitions?: LikutBarcodeDefinition[]; barcodeToProducts?: LikutProductBarcode[] }) {
		if (options.barcodeDefinitions) {
			localStorage.setItem('barcodeDefinitions', compress(JSON.stringify(options.barcodeDefinitions)));
		}
		if (options.barcodeToProducts) {
			localStorage.setItem('barcodeToProducts', compress(JSON.stringify(options.barcodeToProducts)));
		}
	}

	async handleGetBarcodeDefinitions(destroy$: Observable<void>) {
		return new Promise<void>(resolve => {
			this.getBarcodeDefinitions()
				.pipe(
					catchError(() => throwError(() => resolve())),
					takeUntil(destroy$),
				)
				.subscribe({
					next: res => {
						if (typeof res === 'string') return resolve();

						this.barcodeDefinitions$.next(res.recordset);

						this.updateStorage({ barcodeDefinitions: res.recordset });

						resolve();
					},
					complete: () => {
						resolve();
					},
				});
		});
	}

	async handleGetBarcodeToProducts(destroy$: Observable<void>) {
		return new Promise<void>(resolve => {
			this.getBarcodeToProducts()
				.pipe(
					catchError(() => throwError(() => resolve())),
					takeUntil(destroy$),
				)
				.subscribe({
					next: res => {
						if (typeof res === 'string') return resolve();

						this.barcodeToProducts$.next(res.recordset);

						this.updateStorage({ barcodeToProducts: res.recordset });

						resolve();
					},
					complete: () => {
						resolve();
					},
				});
		});
	}
	//likut-scan
	extendLikutFinishBodyWithScanTable(likutBody: LikutFinishBodyNewRoutes[]) {
		const scans = this.likutBarcodeScans$.value;
		const scanTable = scans.map((scan): LikutFinishBodyNewRoutes => {
			const { productId, qty, packQty, tokef, packageSug, packId, rowId, eitur, teudanum, scanNum, timestamp, originalBarcode, source } = scan;

			return {
				DocId: teudanum,
				PackageSug: packageSug,
				ProductId: productId,
				RowId: String(rowId),
				Tokef: tokef,
				PackQTY: packQty,
				QTY: qty,
				ScanNum: scanNum,
				ScanTime: translateDateForSQLServer(new Date(timestamp)),
				table: 'scans',
				PackID: packId,
				TeudaNum: teudanum,
				TeudaType: 1, // garbage value, unimportant for now
				Eitur: eitur,
				Source: source,
				OriginalBarcode: originalBarcode,
			};
		});
		for (const line of likutBody.filter(line => line.table === 'sub')) {
			const subLine = line as LikutFinishBodyNewRoutesSubTable;
			const { TeudaLineNum, TeudaNum } = subLine;
			const scansForLine = (scanTable as LikutFinishBodyNewRoutesScansTable[]).filter(scan => String(scan.RowId) === String(TeudaLineNum) && scan.DocId === TeudaNum);
			const quantity = scansForLine.reduce((acc, scan) => acc + scan.QTY, 0);
			const packs = scansForLine.reduce((acc, scan) => acc + scan.PackQTY, 0);
			subLine.LikutQty += quantity;
			subLine.LikutPackQty += packs;
		}
		return likutBody.concat(scanTable);
	}
	extendLikutFinishBodyMekabelWithScanTable<T extends LikutMekabelFinishBody | LikutMekabelFinishBodyForUnexistingTask>(likutBody: T[]): T[] {
		const scans = this.likutBarcodeScans$.value;
		const scanTable = scans.map((scan): T => {
			const { productId, qty, packQty, tokef, packageSug, packId, rowId, eitur, teudanum, scanNum, timestamp, originalBarcode, source } = scan;

			return {
				DocId: teudanum,
				PackageSug: packageSug,
				ProductId: productId,
				RowId: String(rowId),
				Tokef: tokef,
				PackQTY: packQty,
				QTY: qty,
				ScanNum: scanNum,
				ScanTime: translateDateForSQLServer(new Date(timestamp)),
				table: 'scans',
				PackID: packId,
				TeudaNum: teudanum,
				TeudaType: 1, // garbage value, unimportant for now
				Eitur: eitur,
				Source: source,
				OriginalBarcode: originalBarcode,
			} as T;
		});
		for (const line of likutBody.filter(line => line.table === 'sub')) {
			const subLine = line as LikutMekabelFinishBodySubTableForNewTask;
			const { TeudaLineNum, TeudaNum } = subLine;
			const scansForLine = (scanTable as LikutFinishBodyNewRoutesScansTable[]).filter(scan => String(scan.RowId) === String(TeudaLineNum) && scan.DocId === TeudaNum);
			const quantity = scansForLine.reduce((acc, scan) => acc + scan.QTY, 0);
			const packs = scansForLine.reduce((acc, scan) => acc + scan.PackQTY, 0);
			subLine.TeudaQtyKabala += quantity;
			subLine.TeudaPackQtyKabala += packs;
		}
		return likutBody.concat(scanTable) as T[];
	}

	extendLikutSfiraBodyWithScans(likutBody: LikutSfiraPayload[]): LikutSfiraPayload[] {
		const scans = this.likutBarcodeScans$.value;
		const scanTable = scans.map((scan): LikutSfiraScansPayload => {
			const { productId, qty, packQty, tokef, packageSug, packId, rowId, eitur, teudanum, scanNum, timestamp, originalBarcode, source } = scan;
			return {
				PackageSug: packageSug,
				Count: 1,
				ProductId: productId,
				RowId: String(rowId),
				Tokef: tokef ? new Date(tokef) : null,
				PackQTY: packQty,
				QTY: qty,
				ScanNum: scanNum,
				ScanTime: translateDateForSQLServer(new Date(timestamp)),
				table: 'scans',
				PackID: packId,
				Eitur: eitur,
				Source: source,
				OriginalBarcode: originalBarcode,
			};
		});

		for (const line of likutBody.filter(line => line.table === 'sub')) {
			const subLine = line as LikutSfiraSubPayload;
			const { LineNum } = subLine;

			const scansForLine = scanTable.filter(scan => String(scan.RowId) === String(LineNum));
			const quantity = scansForLine.reduce((acc, scan) => acc + scan.QTY, 0);
			const packs = scansForLine.reduce((acc, scan) => acc + scan.PackQTY, 0);
			if (subLine.LikutQty || subLine.LikutPackQty) {
				scanTable.push({
					PackageSug: null,
					Count: 1,
					ProductId: subLine.ProductID,
					RowId: String(subLine.LineNum),
					Tokef: null,
					PackQTY: subLine.LikutPackQty,
					QTY: subLine.LikutQty,
					ScanNum: scansForLine.length + 1,
					ScanTime: new Date(),
					table: 'scans',
					PackID: null,
					Eitur: subLine.Eitur,
					Source: scanHistoryOriginSource.beforeSend_translatedQuantity,
					OriginalBarcode: null,
				});
			}
			subLine.LikutQty += quantity;
			subLine.LikutPackQty += packs;
		}

		return (scanTable as LikutSfiraPayload[]).concat(likutBody);
	}

	init() {
		this.getBarcodeInformationFromStorage();
		this.clearScanHistory();
		this.handleRefreshParameters();
	}

	handleRefreshParameters() {
		if (!this._ss.CustomerInformation) return;

		if (this._ss.CustomerInformation.Likut_DisableScanWhenPopupOpen === '1') {
			this.parameters.shouldIgnoreScanListeningWhilePopupOpen = true;
		}
	}

	handleOpenScanHistoryPopup(task: LikutSubSQL | LikutKabalaSubSQL) {
		this.refreshFilter(task);
		this.dialog.open(LikutTaskScanHistoryComponent, {
			width: '100%',
			maxWidth: 'unset',
		});
	}

	handleOpenManuallyAddScanPopup(task: LikutSubSQL | LikutKabalaSubSQL) {
		this.dialog.open(LikutTaskManuallyAddScanComponent, {
			width: '100%',
			maxWidth: 'unset',
			data: task,
		});
	}

	refreshFilter(task: LikutSubSQL | LikutKabalaSubSQL) {
		const filter = task.TeudaNum + task.TeudaLineNum;
		this.filterScansBy$.next(filter);
	}

	resetFilter() {
		this.filterScansBy$.next(null);
	}

	handleRemoveFromScanHistory(scanId: string) {
		this.likutBarcodeScans$.next(this.likutBarcodeScans$.value.filter(scan => scan.uuid !== scanId));
	}

	getBarcodeInformationFromStorage() {
		const barcodeDefinitions = localStorage.getItem('barcodeDefinitions');
		const barcodeToProducts = localStorage.getItem('barcodeToProducts');

		if (barcodeDefinitions) {
			this.barcodeDefinitions$.next(JSON.parse(decompress(barcodeDefinitions)));
		}
		if (barcodeToProducts) {
			this.barcodeToProducts$.next(JSON.parse(decompress(barcodeToProducts)));
		}
	}

	// initial filter and match
	getBarcodesDefinitions(
		likutTask: (LikutSubSQL | LikutKabalaSubSQL)[],
		options: { shouldFilterByRelevantTask?: boolean } = { shouldFilterByRelevantTask: true },
	): LikutProductBarcodeWithDefinition[] {
		const { shouldFilterByRelevantTask } = options;
		// const definitions = this.barcodeDefinitions$.value;
		const definitionsDictionary = this.barcodeDefinitions$.value.reduce((acc, definition) => {
			acc[definition.Id] = definition;
			return acc;
		}, {} as Record<number, LikutBarcodeDefinition>);
		let barcodeToProducts = this.barcodeToProducts$.value;

		if (shouldFilterByRelevantTask) {
			const productIDs = likutTask.map(task => task.ProductId);
			barcodeToProducts = barcodeToProducts.filter(barcodeToProduct => productIDs.includes(barcodeToProduct.ProductId));
		}

		return barcodeToProducts.map(barcodeToProduct => {
			const definition = definitionsDictionary[barcodeToProduct.BarcodeDefId];
			return { definition, productWithBarcode: barcodeToProduct };
		});
	}

	// handle user scan
	handleScanBarcodeCheckAndAddToHistory(payload: {
		barcode: string;
		source: ScanHistoryOriginSource;
		productWithBarcodeDefinitions: LikutProductBarcodeWithDefinition[];
		checkboxStateDictionary: Record<string, boolean>;
	}): LikutBarcodeScanItem | null {
		const { barcode, productWithBarcodeDefinitions, source, checkboxStateDictionary } = payload;
		const productWithBarcodeDefinition = this.matchBarcodeToCorrectDefinition(barcode, productWithBarcodeDefinitions);
		if (!productWithBarcodeDefinition) return null;

		const { definition, productWithBarcode } = productWithBarcodeDefinition;
		try {
			const scanInfo = this.translateBarcodeToScanInfo({ barcode, definition, barcodeToProduct: productWithBarcode, source });

			// TODO integrate with `getLineIdentifier` instead of manually building the key.
			// This invalidates the SOLID principle, as the checkboxStateDictionary should only be accessed with the key from `getLineIdentifier`.
			if (scanInfo && checkboxStateDictionary[`${scanInfo.productId}-${scanInfo.teudanum}-${scanInfo.rowId}`]) {
				throw new IllegalActionError('product already checked');
			}

			this.validatePackageIDDuplication(scanInfo);
			this.likutBarcodeScans$.next([scanInfo, ...this.likutBarcodeScans$.value]);
			return scanInfo;
		} catch (error) {
			if (error instanceof ValidationError) {
				console.error('Error in adding barcode scan to history:', error);
				return null;
			}
			if (error instanceof DuplicationError || error instanceof IllegalActionError) {
				throw error;
			}
			console.error('Unkown error:', error);
			return null;
		}
	}

	handleCheckScanBarcodeForMoreThanOneDefinition(payload: { barcode: string; productWithBarcodeDefinitions: LikutProductBarcodeWithDefinition[] }): LikutProductBarcodeWithDefinition[] {
		const { barcode, productWithBarcodeDefinitions } = payload;
		return this.matchBarcodeToAnyCorrectDefinitions(barcode, productWithBarcodeDefinitions);
	}

	matchBarcodeToCorrectDefinition(barcode: string, productWithBarcodeDefinitions: LikutProductBarcodeWithDefinition[]): LikutProductBarcodeWithDefinition | undefined {
		return productWithBarcodeDefinitions.find(({ productWithBarcode, definition }) => this.isCorrectBarcodeForDefinition(barcode, definition, productWithBarcode));
	}

	matchBarcodeToAnyCorrectDefinitions(barcode: string, productWithBarcodeDefinitions: LikutProductBarcodeWithDefinition[]): LikutProductBarcodeWithDefinition[] {
		return productWithBarcodeDefinitions.filter(({ productWithBarcode, definition }) => this.isCorrectBarcodeForDefinition(barcode, definition, productWithBarcode));
	}

	isCorrectBarcodeForDefinition(barcode: string, definition: LikutBarcodeDefinition, barcodeToProduct: LikutProductBarcode): boolean {
		if (!definition || !barcodeToProduct) return false;
		const { BarcodeLen, MakatLen, MakatLoc } = definition;
		const { IdFromBarcode } = barcodeToProduct;

		if (!BarcodeLen || !MakatLen || !MakatLoc || !barcode.length) return false;

		return barcode.length === BarcodeLen && this.barcodeToMakat({ barcode, definition }) === IdFromBarcode;
	}

	translateBarcodeToScanInfo(payload: { barcode: string; source: ScanHistoryOriginSource; definition: LikutBarcodeDefinition; barcodeToProduct: LikutProductBarcode }): LikutBarcodeScanItem {
		const { barcode, definition, barcodeToProduct, source } = payload;
		const { ProductId } = barcodeToProduct;
		const { PackageSug, QtyDot, QtyLen, QtyLoc, PackQTY } = definition;

		let quantity = this.barcodeToQuantity({ barcode, location: QtyLoc, length: QtyLen, dot: QtyDot });
		let packQuantity = this.barcodeToQuantity({ barcode, location: PackQTY, length: 1, dot: 1 }) || 1;

		if (barcodeToProduct.QTYDefault) {
			quantity = barcodeToProduct.QTYDefault;
		}
		if (barcodeToProduct.QTYPackDefault) {
			packQuantity = barcodeToProduct.QTYPackDefault;
		}

		let tokef = null;

		if (definition.TokefDay && definition.TokefMonth && definition.TokefYear) {
			tokef = translateDateForSQLServer(this.barcodeToTokef({ barcode, definition }));
		}

		const timestamp = Date.now();

		const scanNum = this.likutBarcodeScans$.value.filter(scan => scan.productId === ProductId).length + 1;

		const packId = this.barcodeToPackId({ barcode, definition });

		const scanItem: LikutBarcodeScanItem = {
			packQty: packQuantity,
			tokef,
			packageSug: PackageSug,
			packId,
			productId: ProductId,
			qty: quantity,
			rowId: '0',
			timestamp,
			scanNum,
			eitur: null,
			teudanum: '',
			uuid: v4(),
			source,
			originalBarcode: barcode,
		};
		let rowId: string, teudanum: string;

		if (this._likut.likutOpenedNewRoutesState.sub.length) {
			const { TeudaLineNum, TeudaNum } = this.handleGetTeudaLineNumFromOpenedTaskLikutSub(scanItem);
			rowId = TeudaLineNum;
			teudanum = TeudaNum;
		}

		if (this._likut.likutMekabelOpenedState.main.rows.length) {
			const { TeudaLineNum, TeudaNum } = this.handleGetTeudaLineNumFromOpenedTaskLikutSubKabala(scanItem);
			rowId = TeudaLineNum;
			teudanum = TeudaNum;
		}

		if (!rowId || !teudanum) {
			throw new ValidationError('no rowId or teudanum found');
		}

		const eitur = this._likut.likutOpenedNewRoutesState.sub.find(product => String(product.TeudaLineNum) === String(rowId))?.Eitur || null;

		return { ...scanItem, rowId, eitur, teudanum };
	}

	validatePackageIDDuplication(scanItem: LikutBarcodeScanItem): void {
		const { packId, productId } = scanItem;

		if (!packId) return;

		if (this.likutBarcodeScans$.value.some(scan => scan.packId === packId && scan.productId === productId)) {
			throw new DuplicationError('package id already scanned');
		}
	}

	handleGetTeudaLineNumFromOpenedTaskLikutSub(scanItem: LikutBarcodeScanItem): { TeudaLineNum: string; TeudaNum: string } {
		const { productId } = scanItem;
		const relevantProducts = this._likut.likutOpenedNewRoutesState.sub.filter(product => product.ProductId === productId);

		if (!relevantProducts.length) {
			throw new ValidationError('no relevant products found');
		}

		const anyPackages = relevantProducts.some(product => product.TeudaPackQuantity > 0);

		if (anyPackages) {
			const relevantScans = this.likutBarcodeScans$.value
				.filter(scan => scan.productId === productId)
				.reduce((acc, scan) => {
					const key = scan.teudanum + scan.rowId;
					acc[key] ||= 0;
					acc[key] += scan.packQty;
					return acc;
				}, {});
			const lineWithUnfulfilledPackages = relevantProducts.find(p => p.TeudaPackQuantity > (p.LikutPackQty || 0) + (relevantScans[p.TeudaNum + p.TeudaLineNum] || 0));

			const { TeudaLineNum, TeudaNum } = lineWithUnfulfilledPackages ? lineWithUnfulfilledPackages : relevantProducts[relevantProducts.length - 1];

			return { TeudaLineNum: String(TeudaLineNum), TeudaNum };
		}

		const anySingles = relevantProducts.some(product => product.TeudaQuantity > 0);

		if (anySingles) {
			const relevantScans = this.buildScanDictionary(productId);
			const totalQuantity = {};
			for (const key in relevantScans) {
				totalQuantity[key] = relevantScans[key].reduce((acc, scan) => acc + scan.qty, 0);
			}
			const lineWithUnfulfilledSingles = relevantProducts.find(p => p.TeudaQuantity > (p.LikutQty || 0) + (totalQuantity[p.TeudaNum + p.TeudaLineNum] || 0));

			const { TeudaLineNum, TeudaNum } = lineWithUnfulfilledSingles ? lineWithUnfulfilledSingles : relevantProducts[relevantProducts.length - 1];

			return { TeudaLineNum: String(TeudaLineNum), TeudaNum };
		}

		const allProductsWithZeroQuantity = relevantProducts.every(product => product.TeudaQuantity === 0 && product.TeudaPackQuantity === 0);

		if (allProductsWithZeroQuantity) {
			// if all products have 0 quantity, treat product as scanned product, and just add to it. no need to check for quantities.
			// this is a special case for products that are not in the task, but are scanned and added to the task by the user.
			const { TeudaLineNum, TeudaNum } = relevantProducts[0];
			return { TeudaLineNum: String(TeudaLineNum), TeudaNum };
		}
		throw new ValidationError('item with no quantities found');
	}

	handleGetTeudaLineNumFromOpenedTaskLikutSubKabala(scanItem: LikutBarcodeScanItem): { TeudaLineNum: string; TeudaNum: string } {
		// no duplicates in kabala, so no need to check for quantities, just return the first product found
		const { productId } = scanItem;
		const relevantProducts = this._likut.likutMekabelOpenedState.main.rows.find(product => product.ProductId === productId);

		if (!relevantProducts) {
			throw new ValidationError('no relevant products found');
		}

		const { TeudaLineNum, TeudaNum } = relevantProducts;
		return { TeudaLineNum: String(TeudaLineNum), TeudaNum };
	}

	barcodeToMakat(payload: { barcode: string; definition: LikutBarcodeDefinition }): string | null {
		const { barcode, definition } = payload;
		const { MakatLen, MakatLoc } = definition;
		if (!MakatLen || !MakatLoc) return null;

		const loc = MakatLoc - 1;

		return barcode.slice(loc, loc + MakatLen).padEnd(MakatLen, '0');
	}

	barcodeToQuantity(payload: { barcode: string; location?: number; length?: number; dot?: number }): number | null {
		const { barcode, location, length, dot } = payload;
		if (!barcode || location == null || !length || dot == null) return 0;

		const zeroBasedLocation = location - 1;

		const quantitySlice = barcode.slice(zeroBasedLocation, zeroBasedLocation + length);

		return Number(quantitySlice.slice(0, dot) + '.' + quantitySlice.slice(dot));
	}

	barcodeToTokef(payload: { barcode: string; definition: LikutBarcodeDefinition }): string | null {
		const { barcode, definition } = payload;
		const { TokefDay, TokefMonth, TokefYear, YearSegment } = definition;
		if (!TokefDay && !TokefMonth && !TokefYear) return null;
		let res = '';
		if (TokefYear) {
			res += `${barcode.slice(TokefYear - 1, TokefYear - 1 + (YearSegment || 0))}`;
		}
		if (TokefMonth) {
			res += `-${barcode.slice(TokefMonth - 1, TokefMonth + 1)}`;
		}
		if (TokefDay) {
			res += `-${barcode.slice(TokefDay - 1, TokefDay + 1)}/`;
		}
		return res;
	}

	barcodeToPackId(payload: { barcode: string; definition: LikutBarcodeDefinition }): number | null {
		const { barcode, definition } = payload;
		const { PackIdLen, PackIdLoc } = definition;
		if (!PackIdLen || !PackIdLoc) return null;

		const loc = PackIdLoc - 1;

		const sliced = barcode.slice(loc, loc + PackIdLen);

		return sliced ? Number(sliced) : null;
	}

	addCustomBarcodeScan({ task, quantity, source, barcode }: { task: LikutSubSQL | LikutKabalaSubSQL; quantity: number; source: ScanHistoryOriginSource; barcode: string }) {
		const { ProductId, TeudaNum, TeudaLineNum, Eitur } = task;
		const scanNum = this.likutBarcodeScans$.value.filter(scan => scan.productId === ProductId).length + 1;
		const timestamp = Date.now();
		const rowId = String(TeudaLineNum);
		const eitur = Eitur || null;
		const teudanum = TeudaNum;

		const scanItem: LikutBarcodeScanItem = {
			teudanum,
			packQty: 1,
			tokef: null,
			packageSug: null,
			packId: null,
			productId: ProductId,
			qty: quantity,
			rowId,
			timestamp,
			scanNum,
			eitur,
			uuid: v4(),
			source,
			originalBarcode: barcode,
		};

		this.likutBarcodeScans$.next([scanItem, ...this.likutBarcodeScans$.value]);
	}

	clearItemFromScanHistory(task: LikutSubSQL | LikutKabalaSubSQL) {
		const { ProductId, TeudaNum, TeudaLineNum } = task;
		const scans = this.likutBarcodeScans$.value.filter(scan => scan.productId !== ProductId || scan.teudanum !== TeudaNum || scan.rowId !== String(TeudaLineNum));
		this.likutBarcodeScans$.next(scans);
	}

	clearScanHistory() {
		this.likutBarcodeScans$.next([]);
	}

	buildScanDictionary(productId?: string): Record<string, LikutBarcodeScanItem[]> {
		let scans = this.likutBarcodeScans$.value;
		if (productId) {
			scans = scans.filter(scan => scan.productId === productId);
		}
		return scans.reduce((acc, scan) => {
			const key = this.getScanIdentifierForTask(scan);
			acc[key] ||= [];
			acc[key].push(scan);
			return acc;
		}, {} as Record<string, LikutBarcodeScanItem[]>);
	}

	isScanForTask(scan: LikutBarcodeScanItem, task: LikutSubSQL | LikutKabalaSubSQL): boolean {
		return this.getScanIdentifierForTask(scan) === this.getTaskIdentifierForScan(task);
	}

	getTaskIdentifierForScan(task: LikutSubSQL | LikutKabalaSubSQL): string {
		return task.TeudaNum + task.TeudaLineNum;
	}

	getScanIdentifierForTask(scan: LikutBarcodeScanItem): string {
		return scan.teudanum + scan.rowId;
	}

	getBarcodeDefinitions() {
		return this._ss.get<LikutBarcodeDefinition>(`${this.pathEntry}/barcode-definitions`);
	}

	getBarcodeToProducts() {
		return this._ss.get<LikutProductBarcode>(`${this.pathEntry}/barcode-to-products`);
	}

	// TODO add correct types, extract _ss methods

	postProduct(body: LikutProductBarcodePayload) {
		return this._ss.post<LikutProductBarcode>(`${this.pathEntry}/barcode-to-products`, body);
	}

	patchProduct(id: number, body: LikutProductBarcodePayload) {
		return this._ss.patch<LikutProductBarcode>(`${this.pathEntry}/barcode-to-products/${id}`, body);
	}

	deleteProduct(id: number) {
		return this._ss.delete<LikutProductBarcode>(`${this.pathEntry}/barcode-to-products/${id}`);
	}

	postBarcodeDefinition(body: LikutBarcodeDefinitionPayload) {
		return this._ss.post<LikutBarcodeDefinition>(`${this.pathEntry}/barcode-definitions`, body);
	}

	patchBarcodeDefinition(id: number, body: LikutBarcodeDefinitionPayload) {
		return this._ss.patch<LikutBarcodeDefinition>(`${this.pathEntry}/barcode-definitions/${id}`, body);
	}

	deleteBarcodeDefinition(id: number) {
		return this._ss.delete<LikutBarcodeDefinition>(`${this.pathEntry}/barcode-definitions/${id}`);
	}
}

export function generateDummyBarcodeFromDefinition(definition: LikutBarcodeDefinition, product?: LikutProductBarcode): string {
	const barcode = Array.from({ length: definition.BarcodeLen }).map(() => '0');

	const { BarcodeLen, MakatLen, MakatLoc, QtyLoc, QtyLen, QtyDot, TokefDay, TokefMonth, TokefYear, YearSegment, PackQTY, PackIdLen, PackIdLoc, PackageSug } = definition;

	if (MakatLen && MakatLoc) {
		for (let i = 0; i < MakatLen; i++) {
			barcode[MakatLoc - 1 + i] = '1';
		}
	}

	if (QtyLoc && QtyLen && QtyDot) {
		for (let i = 0; i < QtyLen; i++) {
			barcode[QtyLoc - 1 + i] = '2';
		}
	}

	if (TokefDay && TokefMonth && TokefYear && YearSegment) {
		const date = new Date();
		const realDay = date.getDate();
		const realMonth = String(date.getMonth() + 1).padStart(2, '0');
		const realYear = String(YearSegment === 4 ? date.getFullYear() : date.getFullYear().toString().slice(2));
		for (let i = 0; i < 2; i++) {
			barcode[TokefDay - 1 + i] = realDay.toString()[i] || '3';
			barcode[TokefMonth - 1 + i] = realMonth[i] || '3';
		}
		for (let i = 0; i < YearSegment; i++) {
			barcode[TokefYear - 1 + i] = realYear[i] || '3';
		}
	}

	if (PackQTY) {
		// TODO logic for this will be added later
		barcode[PackQTY - 1] = '1';
	}

	if (PackIdLen && PackIdLoc) {
		for (let i = 0; i < PackIdLen; i++) {
			barcode[PackIdLoc - 1 + i] = '4';
		}
	}

	if (PackageSug) {
		// TODO logic for this will be added later
		barcode[PackageSug - 1] = '1';
	}

	if (barcode.length !== BarcodeLen) {
		throw new Error('Barcode length mismatch');
	}

	if (product) {
		const { IdFromBarcode } = product;
		barcode.splice(MakatLoc - 1, MakatLen, ...IdFromBarcode.split(''));
	}

	return barcode.join('');
}
